import { useMutation, useQueryClient } from '@tanstack/react-query';
import { shelfMapService } from '@/services/shelfMapService';
import type {
  CreateShelfMapLayoutInput,
  UpdateShelfMapLayoutInput,
  BatchCreateShelfMapSlotInput,
  CreateShelfMapSlotInput,
  UpdateShelfMapSlotInput,
  MoveShelfMapSlotInput,
  ShelfMapLayoutWithSlots,
} from '@/services/shelfMapService';
import { shelfMapQueryKeys } from '@/hooks/queries/shelf-map/useShelfMapLayouts';

// ---------------------------------------------------------------------------
// Layout mutations
// ---------------------------------------------------------------------------

export const useCreateShelfMapLayout = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateShelfMapLayoutInput) => shelfMapService.createLayout(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: shelfMapQueryKeys.layouts() });
    },
  });
};

export const useUpdateShelfMapLayout = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ layoutId, input }: { layoutId: string; input: UpdateShelfMapLayoutInput }) =>
      shelfMapService.updateLayout(layoutId, input),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: shelfMapQueryKeys.layouts() });
      void queryClient.invalidateQueries({
        queryKey: shelfMapQueryKeys.layout(variables.layoutId),
      });
    },
  });
};

export const useDeleteShelfMapLayout = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (layoutId: string) => shelfMapService.deleteLayout(layoutId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: shelfMapQueryKeys.layouts() });
    },
  });
};

// ---------------------------------------------------------------------------
// Slot mutations
// ---------------------------------------------------------------------------

export const useCreateShelfMapSlot = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ layoutId, input }: { layoutId: string; input: CreateShelfMapSlotInput }) =>
      shelfMapService.createSlot(layoutId, input),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: shelfMapQueryKeys.layout(variables.layoutId),
      });
    },
  });
};

export const useBatchCreateShelfMapSlots = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      layoutId,
      slots,
    }: {
      layoutId: string;
      slots: BatchCreateShelfMapSlotInput[];
    }) => shelfMapService.batchCreateSlots(layoutId, slots),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: shelfMapQueryKeys.layout(variables.layoutId),
      });
    },
  });
};

export const useUpdateShelfMapSlot = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      layoutId,
      slotId,
      input,
    }: {
      layoutId: string;
      slotId: string;
      input: UpdateShelfMapSlotInput;
    }) => shelfMapService.updateSlot(layoutId, slotId, input),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: shelfMapQueryKeys.layout(variables.layoutId),
      });
    },
  });
};

export const useDeleteShelfMapSlot = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ layoutId, slotId }: { layoutId: string; slotId: string }) =>
      shelfMapService.deleteSlot(layoutId, slotId),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: shelfMapQueryKeys.layout(variables.layoutId),
      });
    },
  });
};

export const useMoveShelfMapSlot = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      layoutId,
      slotId,
      input,
    }: {
      layoutId: string;
      slotId: string;
      input: MoveShelfMapSlotInput;
    }) => shelfMapService.moveSlot(layoutId, slotId, input),
    onMutate: async (variables) => {
      // Cancel any in-flight refetches so they don't overwrite the optimistic update
      await queryClient.cancelQueries({ queryKey: shelfMapQueryKeys.layout(variables.layoutId) });

      // Snapshot the previous value for rollback
      const previous = queryClient.getQueryData<ShelfMapLayoutWithSlots>(
        shelfMapQueryKeys.layout(variables.layoutId),
      );

      // Immediately move the slot to the new aisle/shelf in the cache
      queryClient.setQueryData<ShelfMapLayoutWithSlots>(
        shelfMapQueryKeys.layout(variables.layoutId),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            slots: old.slots.map((s) =>
              s.id === variables.slotId
                ? {
                    ...s,
                    aisle_label: variables.input.target_aisle_label,
                    shelf_number: variables.input.target_shelf_number,
                  }
                : s,
            ),
          };
        },
      );

      return { previous };
    },
    onError: (_err, variables, context) => {
      // Roll back to the snapshot on error
      if (context?.previous) {
        queryClient.setQueryData(shelfMapQueryKeys.layout(variables.layoutId), context.previous);
      }
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: shelfMapQueryKeys.layout(variables.layoutId),
      });
    },
  });
};
